import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import SaveIcon from '@mui/icons-material/Save';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  DEFAULT_MAINTENANCE_SETTINGS,
  getMaintenanceRoleOptions,
  getMaintenanceSettings,
  updateMaintenanceSettings,
} from './api';
import { MaintenanceRoleOption, MaintenanceSettings } from './types';

const displayTypeOptions = [
  { value: 'tape', label: 'Smal tape överst' },
  { value: 'banner', label: 'Banner' },
  { value: 'popup', label: 'Popupruta' },
] as const;

const severityOptions = [
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Varning' },
  { value: 'critical', label: 'Kritisk' },
] as const;

const toDateTimeInputValue = (value: string | null | undefined): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
};

const fromDateTimeInputValue = (value: string): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const getPreviewSeverity = (severity: MaintenanceSettings['severity']) => {
  if (severity === 'critical') return 'error';
  return severity;
};

const getTapeColors = (severity: MaintenanceSettings['severity']) => {
  if (severity === 'critical') return { bg: '#8B1538', color: '#fff' };
  if (severity === 'info') return { bg: '#0b5cad', color: '#fff' };
  return { bg: '#f57c00', color: '#1f1f1f' };
};

const getPreviewText = (settings: MaintenanceSettings) =>
  `${settings.title ? `${settings.title}: ` : ''}${settings.message || 'Skriv ett meddelande för att förhandsvisa.'}`;

const MaintenancePreview: React.FC<{ settings: MaintenanceSettings }> = ({ settings }) => {
  const previewText = getPreviewText(settings);

  if (settings.displayType === 'popup') {
    return (
      <Box
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: '#f5f7fb',
          minHeight: 260,
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={8}
          sx={{
            width: '100%',
            maxWidth: 520,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <WarningAmberIcon color={getPreviewSeverity(settings.severity) as any} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {settings.title || 'Underhållsmeddelande'}
            </Typography>
          </Box>
          <Box sx={{ px: 3, py: 2.5 }}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {settings.message || 'Skriv ett meddelande för att förhandsvisa.'}
            </Typography>
          </Box>
          <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid', borderColor: 'divider' }}>
            <Button variant="contained" size="small">Stäng</Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (settings.displayType === 'banner') {
    return (
      <Box
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: '#f5f7fb',
          p: 3,
        }}
      >
        <Paper elevation={6} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Alert severity={getPreviewSeverity(settings.severity) as any} sx={{ alignItems: 'center' }}>
            {settings.title && <strong>{settings.title}: </strong>}
            <span>{settings.message || 'Skriv ett meddelande för att förhandsvisa.'}</span>
          </Alert>
        </Paper>
      </Box>
    );
  }

  const colors = getTapeColors(settings.severity);

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: '#f5f7fb',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          minHeight: 32,
          px: 2,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          bgcolor: colors.bg,
          color: colors.color,
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            minWidth: '200%',
            whiteSpace: 'nowrap',
            animation: 'maintenancePreviewTapeMarquee 18s linear infinite',
            '@keyframes maintenancePreviewTapeMarquee': {
              '0%': { transform: 'translateX(0)' },
              '100%': { transform: 'translateX(-50%)' },
            },
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
              transform: 'none',
            },
          }}
        >
          {[0, 1].map((index) => (
            <Typography
              key={index}
              variant="body2"
              component="span"
              sx={{ width: '50%', px: 6, fontWeight: 700, textAlign: 'center' }}
            >
              {previewText}
            </Typography>
          ))}
        </Box>
      </Box>
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Tape-förhandsvisning med samma rullande text som visas i appen.
        </Typography>
      </Box>
    </Box>
  );
};

const MaintenanceSettingsPage: React.FC = () => {
  const [settings, setSettings] = React.useState<MaintenanceSettings>(DEFAULT_MAINTENANCE_SETTINGS);
  const [roles, setRoles] = React.useState<MaintenanceRoleOption[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const roleNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    roles.forEach((role) => map.set(role._id, role.name));
    return map;
  }, [roles]);

  React.useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        const [settingsResponse, rolesResponse] = await Promise.all([
          getMaintenanceSettings(),
          getMaintenanceRoleOptions(),
        ]);

        setSettings({ ...DEFAULT_MAINTENANCE_SETTINGS, ...settingsResponse });
        setRoles(Array.isArray(rolesResponse) ? rolesResponse : []);
      } catch (err: any) {
        console.error('Error loading maintenance settings:', err);
        setError(err.response?.data?.message || 'Kunde inte ladda underhållsinställningar.');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const updateSettings = (patch: Partial<MaintenanceSettings>) => {
    setSettings((current) => ({ ...current, ...patch }));
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const savedSettings = await updateMaintenanceSettings(settings);
      setSettings({ ...DEFAULT_MAINTENANCE_SETTINGS, ...savedSettings });
      setSuccess('Underhållsinställningar sparade.');
    } catch (err: any) {
      console.error('Error saving maintenance settings:', err);
      setError(err.response?.data?.message || 'Kunde inte spara underhållsinställningar.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={240}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100 }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CampaignIcon sx={{ color: '#072f53' }} />
          <Typography variant="h5" sx={{ color: '#072f53', fontWeight: 700 }}>
            Underhåll
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Styr driftmeddelanden som visas i Athena innan planerat underhåll eller korta avbrott.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Stack spacing={3}>
        <Card elevation={2}>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Meddelande
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    När meddelandet är aktivt visas det för valda roller.
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.enabled}
                      onChange={(event) => updateSettings({ enabled: event.target.checked })}
                    />
                  }
                  label={settings.enabled ? 'Aktiv' : 'Inaktiv'}
                />
              </Box>

              <Divider />

              <TextField
                label="Rubrik"
                value={settings.title}
                onChange={(event) => updateSettings({ title: event.target.value })}
                fullWidth
                inputProps={{ maxLength: 120 }}
                helperText="Exempel: Planerat underhåll"
              />

              <TextField
                label="Meddelande"
                value={settings.message}
                onChange={(event) => updateSettings({ message: event.target.value })}
                fullWidth
                multiline
                minRows={3}
                inputProps={{ maxLength: 1000 }}
                helperText={`${settings.message.length}/1000 tecken`}
              />

              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                <TextField
                  select
                  label="Visning"
                  value={settings.displayType}
                  onChange={(event) => updateSettings({ displayType: event.target.value as MaintenanceSettings['displayType'] })}
                  fullWidth
                >
                  {displayTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Nivå"
                  value={settings.severity}
                  onChange={(event) => updateSettings({ severity: event.target.value as MaintenanceSettings['severity'] })}
                  fullWidth
                >
                  {severityOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                <TextField
                  label="Starttid"
                  type="datetime-local"
                  value={toDateTimeInputValue(settings.startsAt)}
                  onChange={(event) => updateSettings({ startsAt: fromDateTimeInputValue(event.target.value) })}
                  InputLabelProps={{ shrink: true }}
                  helperText="Lämna tomt för att visa direkt."
                  fullWidth
                />

                <TextField
                  label="Sluttid"
                  type="datetime-local"
                  value={toDateTimeInputValue(settings.endsAt)}
                  onChange={(event) => updateSettings({ endsAt: fromDateTimeInputValue(event.target.value) })}
                  InputLabelProps={{ shrink: true }}
                  helperText="Lämna tomt för att stänga av manuellt."
                  fullWidth
                />
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.dismissible}
                    onChange={(event) => updateSettings({ dismissible: event.target.checked })}
                  />
                }
                label="Användare får stänga meddelandet"
              />
            </Stack>
          </CardContent>
        </Card>

        <Card elevation={2}>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Målgrupp
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Välj vilka roller som ska se meddelandet. Om ingen roll väljs visas det för alla inloggade användare.
                </Typography>
              </Box>

              <FormControl fullWidth>
                <InputLabel id="maintenance-role-audience-label">Visa för roller</InputLabel>
                <Select
                  labelId="maintenance-role-audience-label"
                  multiple
                  value={settings.audienceRoleIds}
                  onChange={(event) => {
                    const value = event.target.value;
                    updateSettings({
                      audienceRoleIds: typeof value === 'string' ? value.split(',') : value,
                    });
                  }}
                  input={<OutlinedInput label="Visa för roller" />}
                  renderValue={(selected) => {
                    const roleIds = selected as string[];
                    if (roleIds.length === 0) return 'Alla inloggade användare';
                    return roleIds.map((roleId) => roleNameById.get(roleId) || roleId).join(', ');
                  }}
                >
                  {roles.map((role) => (
                    <MenuItem key={role._id} value={role._id}>
                      <Checkbox checked={settings.audienceRoleIds.includes(role._id)} />
                      <ListItemText primary={role.name} secondary={role.description} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {settings.audienceRoleIds.length === 0 ? (
                  <Chip label="Alla användare" color="primary" variant="outlined" />
                ) : (
                  settings.audienceRoleIds.map((roleId) => (
                    <Chip key={roleId} label={roleNameById.get(roleId) || roleId} />
                  ))
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card elevation={2}>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Förhandsvisning
              </Typography>
              <MaintenancePreview settings={settings} />
              <Typography variant="body2" color="text.secondary">
                Förhandsvisningen använder vald visning, nivå, rubrik och text. Rollurval och tider påverkar bara vilka som ser meddelandet i skarpt läge.
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Sparar...' : 'Spara underhåll'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default MaintenanceSettingsPage;
